import { useMemo, useCallback } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { useSystemAlerts } from "../hooks/useSystemAlerts"
import { useSystemInfo } from "../providers/helpers/useSystem"
import { AlertsKanbanView } from "@hydrologiq/shared/components/kanban"
import type { AlertsKanbanData } from "@hydrologiq/shared/components/kanban"
import { AssetConfig } from "@hydrologiq/shared/monitoring/builders/2.0.0/graphs/TimeSeries"

function SystemAlertsPerformance() {
  const { systemId } = useParams<{ systemId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const highlightParam = searchParams.get("highlight")

  const { data, loading, error } = useSystemAlerts(systemId!)
  const info = useSystemInfo()

  const isAssetId = useMemo(() => highlightParam && !highlightParam.startsWith("alert-"), [highlightParam])
  const highlightedAlertId = useMemo(() => (isAssetId ? undefined : highlightParam || undefined), [isAssetId, highlightParam])
  const highlightedAssetId = useMemo(() => (isAssetId ? highlightParam || undefined : undefined), [isAssetId, highlightParam])

  const assetConfigs: AssetConfig[] = useMemo(() => {
    const assetEntries = Object.entries(info?.assets || {})
    return assetEntries
      .filter(([, asset]) => {
        const isFuelTank = asset.name?.toLowerCase().includes('tank')
        return !isFuelTank
      })
      .map(([assetId, asset]) => ({
        assetId,
        label: asset.name,
        strokeWidth: 2,
        dot: false,
        connectNulls: true,
        group: asset.location?.name || undefined,
      }))
  }, [info?.assets])

  const handleHighlightDismiss = useCallback(() => {
    setSearchParams({})
  }, [setSearchParams])

  return (
    <AlertsKanbanView
      data={data as AlertsKanbanData | undefined}
      loading={loading}
      error={error}
      systemId={systemId}
      systemView={true}
      highlightedAlertId={highlightedAlertId}
      highlightedAssetId={highlightedAssetId}
      onHighlightDismiss={handleHighlightDismiss}
      assetConfigs={assetConfigs}
    />
  )
}

export default SystemAlertsPerformance
